// Navigation Kernel – centrální orchestrátor navigačního jádra

import { AgendaRegistry } from './agendaRegistry';
import type { AgendaHandle } from './agendaDefinition';
import type { LayerDescriptor } from './layerDescriptor';
import { createLayerDescriptor } from './layerDescriptor';
import type { LayerPresentation, LayerZone } from './navigationTypes';
import type { NavigationState } from './navigationState';
import {
  createInitialNavigationState,
  pushLayer,
  popLayer,
  closeLayersAbove,
  removeLayer,
  replaceWithRoot,
  replaceTopLayer,
  replaceDockedLayersAbove,
  updateLayerState,
  setLayerStackId,
} from './navigationState';
import type { RouterAdapter } from './routerAdapter';
import { serializeStack } from './navigationSerializer';
import type { ParamCodecMap, ParamInput } from './paramCodec';
import { restoreFromUrl } from './restoreFromUrl';

/** Callback pro notifikaci o změně navigačního stavu */
export type NavigationStateListener = (state: NavigationState) => void;

/**
 * Callback volaný při OTEVŘENÍ nové vrstvy
 * (openRoot/openModal/openDetail/openOverlayRoot/replaceModal).
 * Dostane descriptor právě otevřené vrstvy. Slouží neutrálním pozorovatelům otevírání agend
 * (např. zápis do historie naposledy otevřených agend) — jádro samo historii nezná.
 */
export type LayerOpenListener = (layer: LayerDescriptor) => void;

/**
 * Guard registrovaný pro vrstvu – volaný před zavřením vrstvy.
 * Vrací true = povolení zavření, false = zamítnutí (navigace se neprovede).
 */
export type LayerCloseGuard = () => Promise<boolean>;

/** Konfigurace pro vytvoření kernelu */
export interface NavigationKernelConfig {
  readonly router: RouterAdapter;
}

/** Volby otevření vrstvy. */
export interface OpenLayerOptions<TState extends ParamCodecMap = ParamCodecMap> {
  /**
   * Počáteční `state` nové vrstvy — intent při otevření (deep-link, „otevři rovnou na záložce X").
   * Dál si stav řídí sama vrstva přes `useAgendaState`.
   */
  readonly state?: Partial<ParamInput<TState>>;
  /** Komponenta agendy; jinak se použije registrovaná přes `registerComponent`. */
  readonly component?: React.ComponentType<{ layerId: string }>;
}

/** Volby otevření detailu vybraného v listu. */
export interface OpenDetailOptions<
  TState extends ParamCodecMap = ParamCodecMap,
> extends OpenLayerOptions<TState> {
  /**
   * `state` klíče, které nový detail PŘEVEZME od detailu, který nahrazuje (má-li je) — typicky
   * zvolená záložka, aby přepnutí na jiný záznam v listu zůstalo na stejné vrstvě dat.
   * Explicitní `state` má přednost. Nahrazuje-li se detail bez daného klíče, nepřevezme se nic.
   */
  readonly inheritStateKeys?: readonly (keyof ParamInput<TState> & string)[];
}

/**
 * Navigation Kernel – factory a orchestrátor navigačního stavu.
 * Drží registr agend, navigační stav a synchronizuje jej s URL.
 */
export class NavigationKernel {
  private readonly registry = new AgendaRegistry();
  private readonly componentMap = new Map<string, React.ComponentType<{ layerId: string }>>();
  private state: NavigationState = createInitialNavigationState();
  private readonly listeners = new Set<NavigationStateListener>();
  private readonly openListeners = new Set<LayerOpenListener>();
  private router: RouterAdapter | null = null;
  private unsubscribeRouter: (() => void) | null = null;

  /** Registry close guardů – klíč = layerId, hodnota = async guard funkce */
  private readonly closeGuards = new Map<string, LayerCloseGuard>();

  /** Připojí router adapter a naslouchá externím změnám URL (browser back/forward) */
  connectRouter(router: RouterAdapter): void {
    this.unsubscribeRouter?.();
    this.router = router;
    this.unsubscribeRouter = router.subscribe(() => {
      this.handleExternalUrlChange();
    });
  }

  /** Odpojí router adapter a přestane naslouchat přes subscribe */
  disconnectRouter(): void {
    this.unsubscribeRouter?.();
    this.unsubscribeRouter = null;
    this.router = null;
  }

  /**
   * Reaguje na změnu URL způsobenou externě (browser back/forward).
   */
  private handleExternalUrlChange(): void {
    if (!this.router) return;

    const currentUrlParams = this.router.getSearchParams();
    const expectedUrlParams = serializeStack(this.state.layers, (agendaId) =>
      this.registry.getById(agendaId)
    );

    if (urlParamsEqual(currentUrlParams, expectedUrlParams)) return;
    this.restoreFromCurrentUrl();
  }

  /** Registruje agendu */
  registerAgenda(config: Parameters<AgendaRegistry['register']>[0]): void {
    this.registry.register(config);
  }

  /** Registruje komponentu pro agendu */
  registerComponent(agendaId: string, component: React.ComponentType<{ layerId: string }>): void {
    this.componentMap.set(agendaId, component);
  }

  /** Vrátí registr */
  getRegistry(): AgendaRegistry {
    return this.registry;
  }

  /** Vrátí aktuální stav */
  getState(): NavigationState {
    return this.state;
  }

  // ── Guard registry ──────────────────────────────────────────────────────────

  /**
   * Zaregistruje close guard pro vrstvu.
   * Vrátí funkci pro odregistraci (volat při unmount komponenty).
   */
  registerCloseGuard(layerId: string, guard: LayerCloseGuard): () => void {
    this.closeGuards.set(layerId, guard);
    return () => {
      this.closeGuards.delete(layerId);
    };
  }

  /** Zkontroluje guard pro jednu vrstvu. Pokud guard neexistuje, vrátí true. */
  private async canCloseLayer(layerId: string): Promise<boolean> {
    const guard = this.closeGuards.get(layerId);
    if (!guard) return true;
    return guard();
  }

  /**
   * Zkontroluje guardy pro seznam vrstev (nejnovější první).
   * Při prvním zamítnutí vrátí false a nepokračuje.
   */
  private async canCloseLayers(layerIds: string[]): Promise<boolean> {
    for (const id of [...layerIds].reverse()) {
      if (!(await this.canCloseLayer(id))) return false;
    }
    return true;
  }

  // ── Navigační operace ───────────────────────────────────────────────────────

  /**
   * Vybere `state` klíče, které nový detail převezme od nahrazovaného detailu plochy.
   * Klíče, které odcházející vrstva nemá (nebo když žádný detail otevřený není), se vynechají.
   */
  private inheritDetailState(keys: readonly string[] | undefined): Record<string, unknown> {
    if (!keys || keys.length === 0) return {};
    const current = this.state.layers.find((l) => l.stackId === undefined && l.zone === 'detail');
    if (!current) return {};
    const inherited: Record<string, unknown> = {};
    for (const key of keys) {
      const value = current.stateParams[key];
      if (value !== undefined) inherited[key] = value;
    }
    return inherited;
  }

  /**
   * Sestaví descriptor nové vrstvy — doplní registrovanou komponentu a počáteční `state`.
   *
   * Počáteční `state` se profiltruje proti `state` kontraktu cílové agendy: vrstva smí nést jen
   * klíče, které agenda deklaruje. Bez toho by nedeklarovaný klíč žil v paměti, ale zmizel po
   * reloadu (serializer emituje jen deklarované) — chování by se lišilo před a po obnovení stránky.
   */
  private createLayer(
    agendaId: string,
    presentation: LayerPresentation,
    input: unknown,
    options: (OpenLayerOptions & { zone?: LayerZone }) | undefined
  ): LayerDescriptor {
    const declared = this.registry.getById(agendaId)?.state;
    const seed = options?.state as Record<string, unknown> | undefined;
    const stateParams =
      seed && declared
        ? Object.fromEntries(Object.entries(seed).filter(([key]) => key in declared))
        : undefined;

    return createLayerDescriptor({
      agendaId,
      presentation,
      ...(options?.zone !== undefined ? { zone: options.zone } : {}),
      openParams: input as Record<string, unknown>,
      ...(stateParams ? { stateParams } : {}),
      component: options?.component ?? this.componentMap.get(agendaId),
    });
  }

  /**
   * Otevře nový root – uzavře staré vrstvy PLOCHY (bez `stackId`).
   * Osamostatněná (floating) okna žijí ve vlastních stacích a zůstávají otevřená,
   * proto se jejich guardy nevolají a vrstvy se nezavírají.
   * Před uzavřením zkontroluje guardy vrstev plochy.
   */
  async openRoot<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen, TState>,
    input: ParamInput<TOpen>,
    options?: OpenLayerOptions<TState>
  ): Promise<void> {
    const plochaLayerIds = this.state.layers
      .filter((l) => l.stackId === undefined)
      .map((l) => l.layerId);
    if (!(await this.canCloseLayers(plochaLayerIds))) return;

    const layer = this.createLayer(agenda.id, 'root', input, options);
    this.state = replaceWithRoot(this.state, layer);
    this.syncToUrl('push');
    this.notifyListeners();
    this.notifyLayerOpened(layer);
  }

  /**
   * Otevře modální vrstvu NAD aktuálním kontextem (push). Guard se NEVOLÁ — nic se nezavírá,
   * podkladová vrstva zůstává otevřená a je se na ni kam vrátit.
   * Zónu (`detail`) nenastavuje ani nedědí: proklik uvnitř detailu je vrstva nad celou agendou,
   * ne výměna obsahu detail panelu. Ten razítkuje výhradně `openDetail`.
   */
  async openModal<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen, TState>,
    input: ParamInput<TOpen>,
    options?: OpenLayerOptions<TState>
  ): Promise<void> {
    const layer = this.createLayer(agenda.id, 'modal', input, options);

    this.state = pushLayer(this.state, layer);
    this.syncToUrl('push');
    this.notifyListeners();
    this.notifyLayerOpened(layer);
  }

  /**
   * Otevře DETAIL vybraný v listu (treeview / toolbar listu) — vrstvu se `zone: 'detail'`,
   * kterou list-detail root vykreslí v pravém panelu.
   *
   * Výběr v listu určuje, co je otevřené, takže se nejdřív zavřou VŠECHNY docked vrstvy nad root
   * vrstvou plochy (předchozí detail i okna nad ním); jejich close guardy musí zavření potvrdit.
   * Osamostatněná (floating) okna žijí ve vlastních stacích a zůstávají otevřená.
   *
   * Mimo plochu s root vrstvou se vrstva jen pushne — příznak zůstane, jen ho layout nemá jak ctít.
   *
   * `inheritStateKeys` nechá nový detail převzít vybraný `state` od detailu, který nahrazuje
   * (např. zvolenou záložku). Vrstvy si tak nemusí navzájem číst parametry — předá je jádro,
   * které jako jediné vidí odcházející i příchozí vrstvu.
   */
  async openDetail<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen, TState>,
    input: ParamInput<TOpen>,
    options?: OpenDetailOptions<TState>
  ): Promise<void> {
    const layer = this.createLayer(agenda.id, 'modal', input, {
      ...options,
      zone: 'detail',
      state: { ...this.inheritDetailState(options?.inheritStateKeys), ...options?.state },
    });

    // Root PLOCHY definuje layout viewportu (osamostatněný root ho nesmí přebít).
    const rootIndex = this.state.layers.findIndex(
      (l) => l.stackId === undefined && l.presentation === 'root'
    );
    if (rootIndex === -1) {
      this.state = pushLayer(this.state, layer);
    } else {
      const root = this.state.layers[rootIndex];
      if (!root) return;
      const dockedAbove = this.state.layers
        .slice(rootIndex + 1)
        .filter((l) => l.stackId === undefined)
        .map((l) => l.layerId);
      if (!(await this.canCloseLayers(dockedAbove))) return;
      this.state = replaceDockedLayersAbove(this.state, root.layerId, layer);
    }

    this.syncToUrl('push');
    this.notifyListeners();
    this.notifyLayerOpened(layer);
  }

  /** Otevře overlay root nad existujícím kontextem */
  async openOverlayRoot<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen, TState>,
    input: ParamInput<TOpen>,
    options?: OpenLayerOptions<TState>
  ): Promise<void> {
    const layer = this.createLayer(agenda.id, 'root', input, options);
    this.state = pushLayer(this.state, layer);
    this.syncToUrl('push');
    this.notifyListeners();
    this.notifyLayerOpened(layer);
  }

  /**
   * Nahradí vrchní modální vrstvu novou modální vrstvou.
   * Zkontroluje guard nahrazované vrstvy. Zónu nahrazované vrstvy zdědí, aby výměna obsahu
   * detail panelu nevystrčila novou vrstvu nad layout.
   */
  async replaceModal<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
    agenda: AgendaHandle<TOpen, TState>,
    input: ParamInput<TOpen>,
    options?: OpenLayerOptions<TState>
  ): Promise<void> {
    const top = this.state.layers[this.state.layers.length - 1];
    const layer = this.createLayer(agenda.id, 'modal', input, {
      ...options,
      ...(top?.presentation === 'modal' && top.zone !== undefined ? { zone: top.zone } : {}),
    });

    if (top?.presentation === 'modal') {
      if (!(await this.canCloseLayer(top.layerId))) return;
      this.state = replaceTopLayer(this.state, layer);
    } else {
      this.state = pushLayer(this.state, layer);
    }

    this.syncToUrl('push');
    this.notifyListeners();
    this.notifyLayerOpened(layer);
  }

  /**
   * Zavře vrchní vrstvu.
   * Zkontroluje guard vrchní vrstvy.
   */
  async closeTop(): Promise<void> {
    const top = this.state.layers[this.state.layers.length - 1];
    if (top && !(await this.canCloseLayer(top.layerId))) return;
    this.state = popLayer(this.state);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zavře všechny vrstvy nad danou vrstvou.
   * Zkontroluje guardy všech uzavíraných vrstev.
   */
  async closeTo(layerId: string): Promise<void> {
    const idx = this.state.layers.findIndex((l) => l.layerId === layerId);
    if (idx < 0) return;
    const layersToClose = this.state.layers.slice(idx + 1).map((l) => l.layerId);
    if (!(await this.canCloseLayers(layersToClose))) return;
    this.state = closeLayersAbove(this.state, layerId);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  /**
   * Zavře konkrétní vrstvu.
   * Zkontroluje guard dané vrstvy.
   */
  async closeLayer(layerId: string): Promise<void> {
    if (!(await this.canCloseLayer(layerId))) return;
    this.state = removeLayer(this.state, layerId);
    this.syncToUrl('push');
    this.notifyListeners();
  }

  // ── State operace (synchronní – neovlivňují close guardy) ──────────────────

  /** Aktualizuje state parametry vrstvy */
  patchLayerState(layerId: string, partialState: Record<string, unknown>): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;

    const newState = { ...layer.stateParams, ...partialState };
    this.state = updateLayerState(this.state, layerId, newState);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /**
   * Nastaví nebo zruší `stackId` vrstvy (osamostatnění / vrácení do plochy).
   * Synchronní operace — neovlivňuje close guardy, jen mění zařazení do stacku.
   * `stackId === undefined` vrátí vrstvu do výchozí plochy.
   */
  setLayerStackId(layerId: string, stackId: string | undefined): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;
    this.state = setLayerStackId(this.state, layerId, stackId);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Resetuje state parametry vrstvy */
  resetLayerState(layerId: string): void {
    this.state = updateLayerState(this.state, layerId, {});
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Odstraní konkrétní state klíče z vrstvy */
  removeLayerStateKeys(layerId: string, keys: string[]): void {
    const layer = this.state.layers.find((l) => l.layerId === layerId);
    if (!layer) return;

    const newState = { ...layer.stateParams };
    for (const key of keys) {
      delete newState[key];
    }
    this.state = updateLayerState(this.state, layerId, newState);
    this.syncToUrl('replace');
    this.notifyListeners();
  }

  /** Nastaví celý stack přímo (pro restore z URL) */
  restoreState(state: NavigationState): void {
    this.state = state;
    this.notifyListeners();
  }

  /** Obnoví stav z aktuální URL */
  restoreFromCurrentUrl(): void {
    if (!this.router) return;
    const searchParams = this.router.getSearchParams();
    const state = restoreFromUrl(searchParams, this.registry);
    this.state = {
      ...state,
      layers: state.layers.map((layer) => {
        const component = this.componentMap.get(layer.agendaId);
        return component ? { ...layer, component } : layer;
      }),
    };
    this.notifyListeners();
  }

  /** Přihlásí listener na změny stavu */
  subscribe(listener: NavigationStateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Přihlásí listener na OTEVŘENÍ nové vrstvy. Vrátí funkci pro odhlášení.
   * Fire pouze u openRoot/openModal/openDetail/openOverlayRoot/replaceModal (ne u zavírání, patch stavu
   * ani obnovy z URL) — restore/reload tedy historii neplní.
   */
  subscribeLayerOpened(listener: LayerOpenListener): () => void {
    this.openListeners.add(listener);
    return () => {
      this.openListeners.delete(listener);
    };
  }

  /** Najde vrstvu podle ID */
  getLayer(layerId: string): LayerDescriptor | undefined {
    return this.state.layers.find((l) => l.layerId === layerId);
  }

  private syncToUrl(action: 'push' | 'replace'): void {
    if (!this.router) return;

    const searchParams = serializeStack(this.state.layers, (agendaId) =>
      this.registry.getById(agendaId)
    );
    this.router.setSearchParams(searchParams, action);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  private notifyLayerOpened(layer: LayerDescriptor): void {
    for (const listener of this.openListeners) {
      listener(layer);
    }
  }
}

/** Factory pro navigační kernel */
export function createNavigationKernel(): NavigationKernel {
  return new NavigationKernel();
}

/** Porovná dva URLSearchParams – order-insensitive */
function urlParamsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const sortedA = [...a.entries()].sort(([k1], [k2]) => k1.localeCompare(k2));
  const sortedB = [...b.entries()].sort(([k1], [k2]) => k1.localeCompare(k2));
  if (sortedA.length !== sortedB.length) return false;
  return sortedA.every(([k, v], i) => {
    const entry = sortedB[i];
    return entry !== undefined && entry[0] === k && entry[1] === v;
  });
}
